import React, { useState, useEffect, useRef } from "react";
import "../App.css";
import "../style.css";
import { Badge } from "react-bootstrap";
import EditTicketModal from "../components/EditTicketModal.jsx";
import OpenTicketModal from "../components/OpenTicketModal.jsx";
import CloseTicketModal from "../components/CloseTicketModal.jsx";
import CreateTicketModal from "../components/CreateTicketModal.jsx";
import ToastNotification from "../components/ToastNotification.jsx";
import Sidebar from "../components/Sidebar.jsx";
import { useNavigate } from "react-router-dom";
import { useGetTicket } from "../hooks/useGetTicket.js";
import { Helmet } from "react-helmet-async";
import Navbar from "../components/Navbar.jsx";
import { useTickets } from "../context/TicketContext";
import { useGetTypesTicket } from "../hooks/useGetTypesTicket.js";
import { IoIosWarning } from "react-icons/io";
import { IoEyeSharp } from "react-icons/io5";
import { FaBookOpen, FaPlus } from "react-icons/fa";
import { RxLockClosed } from "react-icons/rx";
import { useAuth } from "../context/AuthContext.js";
import { FaRepeat } from "react-icons/fa6";
import { getDisplayTitle } from "../utils/titleformat.js";
import { useUserContext } from "../context/UserContext.js";
import BullesIntervenants from "../components/BullesIntervenants.jsx";
import RelanceTicketModal from "../components/askRelanceMessage.jsx";
import { relanceAvaillable } from "../utils/relance.js";

const TICKET_STATUTS = {
  0: "Ouvert",
  1: "En cours",
  2: "Clôture",
  3: "Approuvé",
};

const STATUT_VARIANT = {
  0: "primary",
  1: "warning",
  2: "secondary",
  3: "success",
};

export default function Tickets({ filteredTicketsTest = null }) {
  const [tickets, setTickets] = useState([]);
  const [allTickets, setAllTickets] = useState([]);
  const { userInfo, filtre, setFiltre } = useAuth();
  const [toast, setToast] = useState({ visible: false, type: "success", message: "" });

  const {
    ticketsOuvert, ticketsFerme,
    loadingOuvert, loadingFerme,
    errorOuvert, errorFerme,
    refreshTickets,
    isConnectedToListTicket, setIsConnectedToListTicket,
  } = useTickets();

  useEffect(() => {
    setAllTickets([...ticketsOuvert, ...ticketsFerme]);
  }, [ticketsOuvert, ticketsFerme]);

  const { ticket: detailsTicket, loading: loadingDetails, error: errorDetails, fetchTicket } = useGetTicket();

  // Sidebar
  const [isCollapsed, setIsCollapsed] = useState(false);

  const navigate = useNavigate();

  // Recherche navbar
  const [isFiltered, setIsFiltered] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState(null);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [filteredTickets, setFilteredTickets] = useState(filteredTicketsTest || []);

  // Filtres tableau
  const [searchInTable, setSearchInTable] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const { typesTicket, loading: loadingTypes } = useGetTypesTicket();

  useEffect(() => {
    const sortByDate = (a, b) => {
      const aIsOwn = a.titre?.includes(userInfo?.userName) ? 1 : 0;
      const bIsOwn = b.titre?.includes(userInfo?.userName) ? 1 : 0;
      if (aIsOwn !== bIsOwn) return bIsOwn - aIsOwn;
      return new Date(b.dateDebut ?? 0) - new Date(a.dateDebut ?? 0);
    };
    if (filtre === "ouvert") {
      setTickets([...ticketsOuvert].sort(sortByDate));
    } else if (filtre === "ferme") {
      setTickets([...ticketsFerme].sort(sortByDate));
    }
  }, [filtre, ticketsOuvert, ticketsFerme]);

  useEffect(() => {
    const e = { target: { value: searchTerm } };
    handleSearchChange(e);
  }, [tickets]);

  useEffect(() => {
    if (searchTerm.trim() !== "") setIsFiltered(true);
  }, [searchTerm]);

  useEffect(() => {
    setIsConnectedToListTicket(true);
  }, []);

  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    if (term.trim() === "") {
      setFilteredTickets(allTickets);
      setShowSearchResults(false);
      return;
    }
    const filtered = allTickets.filter(ticket =>
      ticket.titre?.toLowerCase().includes(term.toLowerCase()) ||
      ticket.nomClient?.toLowerCase().includes(term.toLowerCase()) ||
      TICKET_STATUTS[ticket.idStatut]?.toLowerCase().includes(term.toLowerCase()) ||
      typesTicket[ticket.idType]?.toLowerCase().includes(term.toLowerCase())
    );
    setFilteredTickets(filtered);
    setShowSearchResults(true);
  };

  const selectTicket = (ticket) => {
    setSearchTerm(ticket.titre);
    setShowSearchResults(false);
    setFilteredTickets([ticket]);
    navigate(`/ticket/${ticket.idTicket}`);
  };

  const searchResultsRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchResultsRef.current && !searchResultsRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Modals
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [openModalOpen, setOpenModalOpen] = useState(false);
  const [editingTicketId, setEditingTicketId] = useState(null);
  const [closingTicketId, setClosingTicketId] = useState(null);
  const [openTicketId, setOpenTicketId] = useState(null);

  const ticketToEdit  = tickets.find(t => t.idTicket === editingTicketId);
  const ticketToClose = tickets.find(t => t.idTicket === closingTicketId);
  const ticketToOpen  = tickets.find(t => t.idTicket === openTicketId);

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [step, setStep] = useState(25);

  const displayedTickets = tickets.filter(ticket => {
    const term = searchInTable.toLowerCase().trim();
    const matchesSearch = !term ||
      ticket.titre?.toLowerCase().includes(term) ||
      TICKET_STATUTS[ticket.idStatut]?.toLowerCase().includes(term) ||
      typesTicket[ticket.idType]?.toLowerCase().includes(term);
    const ticketDate = ticket.dateDebut ? new Date(ticket.dateDebut) : null;
    const from = dateFrom ? new Date(dateFrom) : null;
    const to = dateTo ? new Date(dateTo + "T23:59:59") : null;
    return matchesSearch &&
      (!from || !ticketDate || ticketDate >= from) &&
      (!to || !ticketDate || ticketDate <= to);
  });

  useEffect(() => { setCurrentPage(0); }, [searchInTable, dateFrom, dateTo, filtre]);

  const totalPages = Math.ceil(displayedTickets.length / step);

  const renderPagination = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(0, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages - 1, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(0, end - maxVisible + 1);

    pages.push(
      <li key="prev" className={`page-item${currentPage === 0 ? " disabled" : ""}`}>
        <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 0}>&laquo;</button>
      </li>
    );
    if (start > 0) {
      pages.push(<li key="first" className="page-item"><button className="page-link" onClick={() => setCurrentPage(0)}>1</button></li>);
      if (start > 1) pages.push(<li key="ell1" className="page-item disabled"><span className="page-link">…</span></li>);
    }
    for (let i = start; i <= end; i++) {
      pages.push(
        <li key={i} className={`page-item${currentPage === i ? " active" : ""}`}>
          <button className="page-link" onClick={() => setCurrentPage(i)}>{i + 1}</button>
        </li>
      );
    }
    if (end < totalPages - 1) {
      if (end < totalPages - 2) pages.push(<li key="ell2" className="page-item disabled"><span className="page-link">…</span></li>);
      pages.push(<li key="last" className="page-item"><button className="page-link" onClick={() => setCurrentPage(totalPages - 1)}>{totalPages}</button></li>);
    }
    pages.push(
      <li key="next" className={`page-item${currentPage === totalPages - 1 ? " disabled" : ""}`}>
        <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages - 1}>&raquo;</button>
      </li>
    );
    return pages;
  };

  // Relance
  const [showRelanceModal, setShowRelanceModal] = useState(false);
  const [selectedTicketRelance, setSelectedTicketRelance] = useState(null);

  return (
    <div className="App">
      <Helmet>
        <title>Tickets – Niralakka</title>
        <meta name="description" content="Liste des tickets" />
      </Helmet>

      <div id="page-top">
        <div id="wrapper" className={isCollapsed ? "sidebar-toggled" : ""}>

          <Sidebar
            onFilterChange={setFiltre}
            isOnTicketPage={true}
            filtre={filtre}
            isCollapsed={isCollapsed}
            setIsCollapsed={setIsCollapsed}
            onCreateTicket={() => setCreateModalOpen(true)}
          />

          <Navbar
            isOnTicketPage={true}
            searchTerm={searchTerm}
            handleSearchChange={handleSearchChange}
            showSearchResults={showSearchResults}
            setShowSearchResults={setShowSearchResults}
            filteredTickets={filteredTickets}
            selectTicket={selectTicket}
            typesTicket={typesTicket}
            TICKET_STATUTS={TICKET_STATUTS}
            isSidebarCollapse={isCollapsed}
          />

          {/* Content Wrapper */}
          <div id="content-wrapper" className="d-flex flex-column">
            <div id="content">
              <div className="container-fluid custom-container">

                <div className="row">
                  <div className="col-12">
                    <div id="dataTableCard" className="card border-left-warning shadow mb-4">

                      {/* Card Header */}
                      <div className="card-header py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <h5 className="m-0 fw-bold text-primary">
                          {filtre === "ouvert" ? "Tickets ouverts" : "Tickets fermés"}
                        </h5>
                        <div className="d-flex align-items-center gap-2" id="buttonsCreateNumberList">
                          <button
                            type="button"
                            onClick={() => setCreateModalOpen(true)}
                            className="btn btn-primary btn-icon-split"
                          >
                            <span className="icon text-white-50"><FaPlus /></span>
                            <span className="text d-none d-xl-inline">Créer un ticket</span>
                          </button>
                          <select
                            className="form-select form-select-sm"
                            style={{ width: "auto" }}
                            value={step}
                            onChange={e => { setStep(parseInt(e.target.value)); setCurrentPage(0); }}
                          >
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                          </select>
                        </div>
                      </div>

                      {/* Filtres tableau */}
                      <div className="px-3 py-2 border-bottom bg-light d-flex flex-wrap align-items-center gap-2">
                        <div className="input-group" style={{ maxWidth: "280px" }}>
                          <span className="input-group-text"><i className="fas fa-search fa-sm" /></span>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Rechercher dans la liste..."
                            value={searchInTable}
                            onChange={e => setSearchInTable(e.target.value)}
                          />
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <span className="text-muted small">Du</span>
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            style={{ width: "150px" }}
                            value={dateFrom}
                            onChange={e => setDateFrom(e.target.value)}
                          />
                          <span className="text-muted small">au</span>
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            style={{ width: "150px" }}
                            value={dateTo}
                            onChange={e => setDateTo(e.target.value)}
                          />
                          {(searchInTable || dateFrom || dateTo) && (
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => { setSearchInTable(""); setDateFrom(""); setDateTo(""); }}
                            >
                              <i className="fas fa-times me-1" />Réinitialiser
                            </button>
                          )}
                        </div>
                      </div>

                      {!loadingOuvert ? (
                        <div className="card-body">
                          <div className="table-responsive">
                            <table className="table ticket-table" id="dataTableTicketOuvert" width="100%">
                              <thead className="table-light">
                                <tr>
                                  <th>Titre</th>
                                  <th>Type</th>
                                  <th>Statut</th>
                                  <th>Intervenant(s)</th>
                                  <th>Date de création</th>
                                  <th>Détails</th>
                                  {filtre === "ouvert" && <th>Relancer</th>}
                                  <th>{filtre === "ouvert" ? "Demander la clôture" : "Rouvrir"}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {loadingOuvert ? (
                                  <tr><td colSpan="8" className="text-center py-4">Chargement...</td></tr>
                                ) : error ? (
                                  <tr><td colSpan="8" className="text-center text-danger">{error}</td></tr>
                                ) : tickets.length === 0 ? (
                                  <tr><td colSpan="8" className="text-center py-4 text-muted">Aucun ticket trouvé</td></tr>
                                ) : (
                                  displayedTickets.slice(step * currentPage, step * currentPage + step).map(ticket => (
                                    <tr key={ticket.idTicket}>
                                      <td style={{ maxWidth: "220px" }}>
                                        <div className="d-flex align-items-center gap-1">
                                          {ticket.priorite === 1 && (
                                            <IoIosWarning style={{ color: "red", fontSize: 18, flexShrink: 0 }} title="Ticket prioritaire" />
                                          )}
                                          <span className="text-truncate" title={getDisplayTitle(ticket.titre)}>
                                            {getDisplayTitle(ticket.titre)?.length > 50
                                              ? getDisplayTitle(ticket.titre).slice(0, 50) + "…"
                                              : getDisplayTitle(ticket.titre)}
                                          </span>
                                        </div>
                                      </td>
                                      <td>{typesTicket[ticket.idType] || <span className="text-muted">N/A</span>}</td>
                                      <td>
                                        <Badge bg={STATUT_VARIANT[ticket.idStatut] ?? "secondary"} text={ticket.idStatut === 1 ? "dark" : undefined}>
                                          {TICKET_STATUTS[ticket.idStatut] || "N/A"}
                                        </Badge>
                                      </td>
                                      <td>
                                        <BullesIntervenants intervenants={ticket.intervenant ?? []} />
                                      </td>
                                      <td className="text-nowrap">
                                        {new Date(ticket.dateDebut).toLocaleString("fr-FR")}
                                      </td>
                                      <td>
                                        <button
                                          className="btn btn-sm btn-outline-secondary"
                                          data-testid="detailsButton"
                                          aria-label="Voir les détails"
                                          onClick={() => navigate(`/ticket/${ticket.idTicket}`)}
                                        >
                                          <IoEyeSharp />
                                        </button>
                                      </td>
                                      {filtre === "ouvert" && (
                                        <td>
                                          {ticket.idStatut <= 1 && (
                                            <button
                                              className={`btn btn-sm ${relanceAvaillable(ticket) ? "btn-primary" : "btn-outline-secondary"}`}
                                              disabled={!relanceAvaillable(ticket)}
                                              aria-label="Relancer"
                                              onClick={() => { setSelectedTicketRelance(ticket); setShowRelanceModal(true); }}
                                              data-testid="relance-button"
                                            >
                                              <FaRepeat />
                                            </button>
                                          )}
                                        </td>
                                      )}
                                      <td>
                                        {ticket.idStatut <= 1 && (
                                          <button
                                            className="btn btn-sm btn-danger"
                                            aria-label="Demander la clôture"
                                            onClick={() => { setClosingTicketId(ticket.idTicket); setCloseModalOpen(true); }}
                                            disabled={
                                              ticket.intervention?.length > 0 &&
                                              ticket.intervention?.at(-1)?.description?.trim()?.split(" ")[0]?.toUpperCase() === "CLOTURE"
                                            }
                                            data-testid="close-button"
                                          >
                                            <RxLockClosed />
                                          </button>
                                        )}
                                        {ticket.idStatut === 2 && (
                                          <button
                                            className="btn btn-sm btn-success"
                                            aria-label="Ré-ouvrir"
                                            onClick={() => { setOpenTicketId(ticket.idTicket); setOpenModalOpen(true); }}
                                            data-testid="open-button"
                                          >
                                            <FaBookOpen />
                                          </button>
                                        )}
                                      </td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                          </div>

                          {/* Pagination */}
                          <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                            <small className="text-muted">
                              {displayedTickets.length === 0 ? "Aucun résultat" : (
                                <>
                                  {Math.min(step * currentPage + 1, displayedTickets.length)}–
                                  {Math.min(step * (currentPage + 1), displayedTickets.length)} sur{" "}
                                  {displayedTickets.length} ticket{displayedTickets.length !== 1 ? "s" : ""}
                                  {(searchInTable || dateFrom || dateTo) && tickets.length !== displayedTickets.length && (
                                    <span> (filtré sur {tickets.length})</span>
                                  )}
                                </>
                              )}
                            </small>
                            <nav aria-label="Pagination">
                              <ul className="pagination pagination-sm mb-0">{renderPagination()}</ul>
                            </nav>
                          </div>
                        </div>
                      ) : (
                        <div style={{ height: "60vh" }} className="d-flex align-items-center justify-content-center">
                          <div className="loader" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

              </div>
            </div>

            <footer className="sticky-footer bg-white">
              <div className="container my-auto">
                <div className="copyright text-center my-auto">
                  <span>Copyright &copy; Niralakka 2025</span>
                </div>
              </div>
            </footer>
          </div>
        </div>

        <a className="scroll-to-top rounded" href="#page-top">
          <i className="fas fa-angle-up" />
        </a>
      </div>

      {/* Modals */}
      {editModalOpen && editingTicketId && (
        <EditTicketModal
          ticket={ticketToEdit}
          ticketDetails={detailsTicket}
          onClose={() => { setEditModalOpen(false); setEditingTicketId(null); }}
          onSubmitSuccess={async () => { setToast({ visible: true, type: "success", message: "Ticket modifié avec succès ✅" }); await refreshTickets(); }}
          onError={() => setToast({ visible: true, type: "error", message: "Echec de la modification ❌" })}
        />
      )}
      {closeModalOpen && closingTicketId && (
        <CloseTicketModal
          ticket={ticketToClose}
          onClose={() => { setCloseModalOpen(false); setClosingTicketId(null); }}
          onSubmitSuccess={async () => { setToast({ visible: true, type: "success", message: "Demande de clôture envoyée ✅" }); await refreshTickets(); }}
          onError={() => setToast({ visible: true, type: "error", message: "Echec de la clôture ❌" })}
        />
      )}
      {openModalOpen && openTicketId && (
        <OpenTicketModal
          ticket={ticketToOpen}
          onClose={() => { setOpenModalOpen(false); setOpenTicketId(null); }}
          onSubmitSuccess={async () => { setToast({ visible: true, type: "success", message: "Ticket réouvert ✅" }); await refreshTickets(); }}
          onError={() => setToast({ visible: true, type: "error", message: "Echec de la réouverture ❌" })}
        />
      )}
      {createModalOpen && (
        <CreateTicketModal
          onClose={() => setCreateModalOpen(false)}
          onSubmitSuccess={async () => { setToast({ visible: true, type: "success", message: "Ticket créé ✅" }); await refreshTickets(); }}
          onError={(error) => setToast({ visible: true, type: "error", message: `Erreur lors de la création. ${error ?? ""}` })}
        />
      )}
      {showRelanceModal && (
        <RelanceTicketModal
          onClose={() => setShowRelanceModal(false)}
          onSubmitSuccess={async () => { setToast({ visible: true, type: "success", message: "Relance effectuée ✅" }); await refreshTickets(); }}
          onError={() => setToast({ visible: true, type: "error", message: "Erreur lors de la relance ❌" })}
          ticket={selectedTicketRelance}
        />
      )}
      {toast.visible && (
        <ToastNotification
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ visible: false })}
        />
      )}
    </div>
  );
}
